import { apiRequest, clearCsrfToken } from './http';

export interface SessionSnapshot {
  userId: string;
  sessionId: string;
  selectedTenantId: string | null;
  selectedTenantSlug: string | null;
  authenticationStrength: string;
  expiresAt: string;
  userName: string;
  displayName: string;
  isSuperAdmin: boolean;
  /** Whether this account may issue the single-use codes required for new registrations. */
  canCreateInvitations: boolean;
  /** The signed-in person's Varza Inter-organization Identity, when they hold one; null decides the portal routing. */
  interOrganizationIdentityCode: string | null;
  formattedInterOrganizationIdentityCode: string | null;
}

export interface AccessibleTenant {
  tenantId: string;
  slug: string;
  displayName: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface MfaMethod {
  id: string;
  type: 'totp' | 'email' | 'sms';
  displayName: string;
  state: 'pending' | 'active';
  maskedDestination: string | null;
}

export type LoginOutcome =
  | Readonly<{ status: 'succeeded' }>
  | Readonly<{ status: 'mfa_required'; methods: readonly MfaMethod[] }>;

interface LoginResponse {
  code?: string;
  userId?: string;
  expiresAt?: string;
  methods?: readonly MfaMethod[];
}

export interface ExternalProviders {
  google: boolean;
  microsoft: boolean;
}

export interface PasswordRecoveryReceipt {
  status: 'recovery_sent';
  /** Development-only shortcut returned when no production mail adapter is configured. */
  developmentToken?: string | null;
}

export interface SecuritySettings {
  mfaMethods: readonly MfaMethod[];
  linkedProviders: readonly string[];
  emailDeliveryAvailable: boolean;
  smsDeliveryAvailable: boolean;
}

export interface MfaEnrollment {
  methodId: string;
  type: MfaMethod['type'];
  displayName: string;
  manualEntryKey?: string;
  provisioningUri?: string;
  developmentCode?: string;
}

export const authApi = {
  me(): Promise<SessionSnapshot> {
    return apiRequest<SessionSnapshot>('/me');
  },

  tenants(): Promise<readonly AccessibleTenant[]> {
    return apiRequest<readonly AccessibleTenant[]>('/tenants');
  },

  async login(credentials: LoginCredentials): Promise<LoginOutcome> {
    const payload = await apiRequest<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password,
      }),
    });

    if (payload.code === 'authentication.mfa_required') {
      return { status: 'mfa_required', methods: payload.methods ?? [] };
    }

    // The anonymous anti-forgery token used for login is identity-bound. Fetch a new one lazily
    // before the first authenticated mutation, such as selecting the tenant workspace.
    clearCsrfToken();
    return { status: 'succeeded' };
  },

  async mfaChallenge(): Promise<readonly MfaMethod[]> {
    const payload = await apiRequest<{ methods: readonly MfaMethod[] }>('/auth/mfa/challenge');
    return payload.methods;
  },

  async sendMfaCode(methodId: string): Promise<{ developmentCode?: string }> {
    return apiRequest<{ developmentCode?: string }>('/auth/mfa/send', {
      method: 'POST',
      body: JSON.stringify({ methodId }),
    });
  },

  async verifyMfa(methodId: string, code: string): Promise<void> {
    await apiRequest('/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ methodId, code }),
    });
    clearCsrfToken();
  },

  providers(): Promise<ExternalProviders> {
    return apiRequest<ExternalProviders>('/auth/providers');
  },

  /** `register` provisions a user and an identity from the provider's verified address on the way back. */
  externalUrl(
    provider: 'google' | 'microsoft',
    mode: 'login' | 'link' | 'register' = 'login',
    invitationCode?: string,
  ): string {
    const query = new URLSearchParams();
    if (mode !== 'login') query.set('mode', mode);
    if (mode === 'register' && invitationCode !== undefined) {
      query.set('invitationCode', invitationCode);
    }
    const suffix = query.size === 0 ? '' : `?${query.toString()}`;
    return `/api/v1/auth/external/${provider}${suffix}`;
  },

  forgotPassword(email: string): Promise<PasswordRecoveryReceipt> {
    return apiRequest<PasswordRecoveryReceipt>('/auth/password/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiRequest<void>('/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
    clearCsrfToken();
  },

  selectTenant(tenantId: string): Promise<SessionSnapshot> {
    return apiRequest<SessionSnapshot>('/auth/select-tenant', {
      method: 'POST',
      body: JSON.stringify({ tenantId }),
    });
  },

  security(): Promise<SecuritySettings> {
    return apiRequest<SecuritySettings>('/auth/security');
  },

  enrollMfa(type: MfaMethod['type'], displayName: string, destination?: string): Promise<MfaEnrollment> {
    return apiRequest<MfaEnrollment>('/auth/security/mfa', {
      method: 'POST',
      body: JSON.stringify({ type, displayName, destination: destination || null }),
    });
  },

  async confirmMfa(methodId: string, code: string): Promise<void> {
    await apiRequest(`/auth/security/mfa/${encodeURIComponent(methodId)}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  async disableMfa(methodId: string): Promise<void> {
    await apiRequest(`/auth/security/mfa/${encodeURIComponent(methodId)}`, { method: 'DELETE' });
  },

  async unlinkProvider(provider: 'google' | 'microsoft'): Promise<void> {
    await apiRequest(`/auth/security/providers/${provider}`, { method: 'DELETE' });
  },

  async logout(): Promise<void> {
    try {
      await apiRequest<void>('/auth/logout', { method: 'POST' });
    } finally {
      clearCsrfToken();
    }
  },
};
